/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * useSlugAvailability — debounced slug uniqueness check.
 */

import { useState, useEffect } from 'react';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Returns { available, checking, usedBy } after a 500ms debounce.
 * `available` is null while loading or when slug is empty.
 */
export function useSlugAvailability(tableId, slug) {
  const [state, setState] = useState({ available: null, checking: false, usedBy: null });

  useEffect(() => {
    if (!slug || slug.trim() === '') {
      setState({ available: null, checking: false, usedBy: null });
      return;
    }

    setState(s => ({ ...s, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const resp = await apiHelper.apiCall(
          `/arcee/application/table/${tableId}/slug-available/?slug=${encodeURIComponent(slug)}`,
          { method: 'GET' }
        );
        if (!resp.ok) {
          setState({ available: null, checking: false, usedBy: null });
          return;
        }
        const data = await resp.json();
        setState({ available: data.available, checking: false, usedBy: data.used_by || null });
      } catch (_) {
        setState({ available: null, checking: false, usedBy: null });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [tableId, slug]);

  return state;
}
